import React, { useState, useEffect } from "react";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import { UserCircle2, TrendingUp, Clock, Wallet, History } from "lucide-react";
import { LOGO_URL } from "@/config";

const money = (n) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n || 0);

// Compact for large totals: 1000 -> 1K, 1100 -> 1.1K, 12500 -> 12.5K
const compact = (n) => {
  n = Number(n || 0);
  if (n >= 1000) {
    const v = n / 1000;
    return (Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1).replace(/\.0$/, "")) + "K";
  }
  return money(n);
};

const pad = (n) => String(n).padStart(2, "0");

export default function WelcomeStatus({ stats, me, onNavigate }) {
  const spark = (stats?.price_spark || []).map((v, i) => ({ i, v }));
  const price = stats?.price_usd ?? 10;
  const stake = Number(me?.stake_usdt || 0);
  const dailyUsd = stake * 0.01; // 0.5% of the 2x mining cap = deposit × 0.01
  const totalEarn = Number(me?.total_profit_usdt || 0); // ROI + level + daily/weekly/monthly combined

  // Live countdown to next 00:00 UTC — when the daily reward generates.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const d = new Date(now);
  const nextUtcMidnight = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1, 0, 0, 0, 0);
  const msLeft = Math.max(0, nextUtcMidnight - now);
  const hh = Math.floor(msLeft / 3600000);
  const mm = Math.floor((msLeft % 3600000) / 60000);
  const ss = Math.floor((msLeft % 60000) / 1000);

  return (
    <div data-testid="welcome-status-card" className="card-glow relative overflow-hidden p-5">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#0AA84F]/14 via-transparent to-[#FFA000]/12" />
      <img
        src={LOGO_URL}
        alt="TITAN"
        onError={(e) => (e.currentTarget.style.display = "none")}
        className="pointer-events-none absolute right-2 top-4 h-24 w-24 rounded-full object-cover opacity-95 shadow-[0_0_24px_rgba(10,168,79,0.4)]"
      />
      <div className="relative z-10 max-w-[68%]">
        <div className="flex items-center gap-2.5">
          <UserCircle2 size={30} className="text-[#34D07A]" />
          <div className="leading-tight">
            <div className="text-xs text-white/50">Welcome back,</div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-white/50">UID</span>
              <span data-testid="welcome-uid" className="font-bold text-[#34D07A]">
                {me?.uid || "—"}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="text-white/50">STATUS</span>
          {me?.is_active ? (
            <span data-testid="welcome-status" className="flex items-center gap-1.5 font-bold text-[#34D07A]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#34D07A]" /> Active
            </span>
          ) : (
            <span data-testid="welcome-status" className="flex items-center gap-1.5 font-bold text-red-400">
              <span className="h-1.5 w-1.5 rounded-full bg-red-400" /> Inactive
            </span>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <div data-testid="total-earning-box" className="flex h-10 w-full items-center justify-between rounded-lg border border-[#34D07A]/45 bg-gradient-to-r from-[#0AA84F]/15 to-[#D6C51E]/10 px-3">
            <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-white/55">
              <Wallet size={13} className="text-[#34D07A]" /> Total Earning
            </span>
            <span data-testid="total-earning-value" className="flex items-baseline gap-1 whitespace-nowrap">
              <span className="text-sm font-bold tabular-nums text-[#34D07A]">${compact(totalEarn)}</span>
              <span className="text-[11px] font-bold text-white/45">USDT</span>
            </span>
          </div>
          <div data-testid="daily-roi-box" className="flex h-10 w-full items-center justify-between rounded-lg border border-[#0AA84F]/40 bg-[#0AA84F]/10 px-3">
            <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-white/55">
              <TrendingUp size={13} className="text-[#34D07A]" /> Daily ROI
            </span>
            <span className="flex items-baseline gap-1">
              <span data-testid="daily-roi-usd" className="text-sm font-bold tabular-nums text-[#34D07A]">${money(dailyUsd)}</span>
              <span className="text-[11px] font-bold text-white/45">/ day</span>
            </span>
          </div>
          <div data-testid="reward-countdown" className="flex h-10 w-full items-center justify-between rounded-lg border border-[#D6C51E]/35 bg-[#D6C51E]/10 px-3">
            <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-white/55">
              <Clock size={13} className="text-[#D6C51E]" /> Next reward
            </span>
            <span className="flex items-baseline gap-1">
              <span data-testid="countdown-value" className="text-sm font-bold tabular-nums text-[#D6C51E]">{pad(hh)}:{pad(mm)}:{pad(ss)}</span>
              <span className="text-[11px] font-bold text-white/45">UTC</span>
            </span>
          </div>
          <button
            data-testid="income-history-box"
            onClick={() => onNavigate?.("mining")}
            className="flex h-10 w-full items-center justify-center gap-1.5 rounded-lg border border-[#34D07A]/45 bg-gradient-to-r from-[#0AA84F]/15 to-[#D6C51E]/10 px-3 text-[11px] font-semibold text-[#34D07A] transition-transform active:scale-[0.98]"
          >
            <History size={13} className="text-[#34D07A]" /> Total Income History
          </button>
        </div>
      </div>

      <div className="relative z-10 mt-3 h-12 w-full" data-testid="price-sparkline">
        <ResponsiveContainer width="99%" height="100%" minWidth={0}>
          <LineChart data={spark} margin={{ top: 8, right: 10, bottom: 4, left: 0 }}>
            <defs>
              <linearGradient id="sparkGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#0AA84F" />
                <stop offset="55%" stopColor="#65B82E" />
                <stop offset="100%" stopColor="#FFC400" />
              </linearGradient>
            </defs>
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <Line
              type="monotone"
              dataKey="v"
              stroke="url(#sparkGrad)"
              strokeWidth={2.5}
              isAnimationActive={false}
              activeDot={false}
              dot={(props) => {
                const { cx, cy, index } = props;
                if (index !== spark.length - 1 || cx == null || cy == null) return null;
                return (
                  <g key="live-dot" data-testid="price-live-dot">
                    <circle cx={cx} cy={cy} r={7} fill="#D6C51E" className="spark-live-glow" />
                    <circle cx={cx} cy={cy} r={3.4} fill="#FFFDE7" stroke="#22C55E" strokeWidth={1.6} />
                  </g>
                );
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
